import { useMemo, type ReactNode } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, ICellRendererParams, RowClassParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

export interface Size2MixSimpleTableColumn<T> {
    key: string;
    label: string;
    align?: 'left' | 'right' | 'center';
    width?: number;
    /** Flex column that never shrinks below this width (grid scrolls horizontally instead). */
    minWidth?: number;
    render?: (row: T, index: number) => ReactNode;
    /** Column filter (menu icon in the header, as in Nemo). Default true. */
    filter?: boolean;
}

export interface Size2MixSimpleTableProps<T> {
    columns: Size2MixSimpleTableColumn<T>[];
    rows: T[];
    /** Shown in the grid body when `rows` is empty (Nemo wording by default). */
    emptyLabel?: string;
    rowKey?: (row: T, index: number) => string;
    onRowClick?: (row: T) => void;
    selectedKey?: string | null;
    /** Enables AG Grid pagination ("Taille de la Page"). */
    pageSize?: number;
    rowHeight?: number;
    headerHeight?: number;
    wrapHeaderText?: boolean;
}

/** « Taille de la Page » options of Nemo grids (measured on Réseau > Import/export, integration 20-10-2026). */
export const SIZE2MIX_AG_GRID_PAGE_SIZES = [15, 30, 50, 100];

export const SIZE2MIX_AG_GRID_LOCALE: Record<string, string> = {
    page: 'Page',
    of: 'de',
    to: 'à',
    next: 'Suivant',
    last: 'Dernier',
    first: 'Premier',
    previous: 'Précédent',
    pageSizeSelectorLabel: 'Taille de la Page :',
    ariaPageSizeSelectorLabel: 'Taille de page',
    firstPage: 'Première Page',
    previousPage: 'Page Précédente',
    nextPage: 'Page Suivante',
    lastPage: 'Dernière Page',
    noRowsToShow: 'Aucune donnée à afficher',
    loadingOoo: 'Chargement...',
    filterOoo: 'Filtrer...',
    equals: 'Égal',
    notEqual: 'Différent',
    contains: 'Contient',
    notContains: 'Ne contient pas',
    startsWith: 'Commence par',
    endsWith: 'Se termine par',
    blank: 'Vide',
    notBlank: 'Non vide',
    andCondition: 'ET',
    orCondition: 'OU',
    lessThan: 'Inférieur à',
    greaterThan: 'Supérieur à',
};

/**
 * AG Grid (quartz theme) read-only table — the look of every Nemo table (synthesis blocks, imports, logs…).
 * Keeps a tiny column API (`key` / `label` / `render`) so prototypes don't need AG Grid ColDefs.
 * For editable grids use `@shared/DataTable` directly.
 */
export function Size2MixSimpleTable<T>({ columns, rows, emptyLabel = 'Aucune donnée à afficher', rowKey, onRowClick, selectedKey, pageSize, rowHeight, headerHeight, wrapHeaderText = true }: Size2MixSimpleTableProps<T>) {
    const columnDefs = useMemo<ColDef<T>[]>(
        () =>
            columns.map((c) => ({
                colId: c.key,
                headerName: c.label,
                valueGetter: (p) => (p.data as Record<string, unknown> | undefined)?.[c.key],
                cellRenderer: c.render ? (p: ICellRendererParams<T>) => (p.data ? c.render!(p.data, p.node.rowIndex ?? 0) : null) : undefined,
                valueFormatter: c.render ? undefined : (p) => (p.value === undefined || p.value === null || p.value === '' ? '-' : String(p.value)),
                filter: c.filter ?? true,
                width: c.width,
                flex: c.width ? undefined : 1,
                minWidth: c.width ? undefined : (c.minWidth ?? 40),
                type: c.align === 'right' ? 'rightAligned' : undefined,
                cellStyle: c.align === 'center' ? { textAlign: 'center' } : undefined,
                autoHeight: rowHeight === undefined,
            })),
        [columns, rowHeight],
    );
    const localeText = useMemo(() => ({ ...SIZE2MIX_AG_GRID_LOCALE, noRowsToShow: emptyLabel }), [emptyLabel]);
    const keyOf = (row: T, i: number) => (rowKey ? rowKey(row, i) : String(i));

    return (
        <div className="ag-theme-quartz" style={{ width: '100%' }}>
            <AgGridReact<T>
                rowData={rows}
                columnDefs={columnDefs}
                defaultColDef={{ sortable: true, resizable: true, suppressMovable: true, wrapHeaderText, autoHeaderHeight: headerHeight === undefined }}
                rowHeight={rowHeight}
                headerHeight={headerHeight}
                domLayout="autoHeight"
                localeText={localeText}
                pagination={pageSize !== undefined}
                paginationPageSize={pageSize}
                paginationPageSizeSelector={pageSize !== undefined ? [15, 50, 100] : false}
                suppressCellFocus
                enableCellTextSelection
                onRowClicked={onRowClick ? (e) => e.data && onRowClick(e.data) : undefined}
                getRowStyle={(p: RowClassParams<T>) => {
                    const style: Record<string, string> = {};
                    if (onRowClick) style.cursor = 'pointer';
                    if (selectedKey != null && p.data && keyOf(p.data, p.node.rowIndex ?? 0) === selectedKey) style.background = 'var(--nj-core-color-blue-alpha-100)';
                    return style;
                }}
            />
        </div>
    );
}
